import { useState } from "react";
import { T, STATE } from "../../config/theme.js";
import { Card, Btn, Input, StateBadge } from "../ui/index.jsx";

export default function ActionModal({ holding, report, defaultAction, onClose, onSave }) {
  const [action] = useState(defaultAction || "profit");
  const [alignment, setAlignment] = useState(null); // "phase" | "personal" | "mixed"
  const [memo, setMemo] = useState("");

  // 매도 입력
  const [sellPrice, setSellPrice] = useState("");
  const [sellQty, setSellQty] = useState(String(holding.qty || ""));
  // 매수 입력
  const [buyPrice, setBuyPrice] = useState("");
  const [buyQty, setBuyQty] = useState("");

  // 액션 종류 판별: add_buy(매수) / partial_sell·profit·cut(매도)
  const isBuy = action === "add_buy";

  const actionInfo = {
    add_buy: { icon: "➕", label: "추가 매수", color: T.green },
    partial_sell: { icon: "➖", label: "일부 매도", color: T.red },
    profit: { icon: "✅", label: "수익 실현", color: T.green },
    cut: { icon: "❌", label: "손절", color: T.red },
  };
  const ai = actionInfo[action] || actionInfo.partial_sell;

  const alignments = [
    { key: "phase", icon: "⭕", label: "Phase 신호 기반", color: T.accent },
    { key: "personal", icon: "❌", label: "개인 판단", color: T.yellow },
    { key: "mixed", icon: "🤔", label: "복합적 이유", color: T.textDim },
  ];

  const avgPrice = holding.avg_price || 0;

  // 매수: 새 평단가 미리보기
  const newAvgPreview = (() => {
    if (!isBuy || !buyPrice || !buyQty) return null;
    const oldTotal = avgPrice * (holding.qty || 0);
    const newTotal = +buyPrice * +buyQty;
    const totalQty = (holding.qty || 0) + +buyQty;
    if (totalQty <= 0) return null;
    return {
      newAvg: Math.round((oldTotal + newTotal) / totalQty),
      totalQty,
    };
  })();

  // 매도: 실현 손익
  const realizedPnl = !isBuy && sellPrice && sellQty ? ((+sellPrice - avgPrice) * +sellQty) : 0;
  const realizedPnlPct = !isBuy && sellPrice && avgPrice ? (((+sellPrice - avgPrice) / avgPrice) * 100) : 0;

  const canSave = isBuy
    ? (alignment && buyPrice && buyQty)
    : (alignment && sellPrice && sellQty);

  const save = () => {
    if (!alignment) return;

    if (isBuy) {
      if (!buyPrice || !buyQty) return alert("매수 가격과 수량을 입력해주세요");
      onSave({
        symbol: holding.symbol,
        name: holding.name,
        action_type: "add_buy",
        alignment,
        phase_state: report?.state_stock || "WATCH",
        memo,
        avg_price: +buyPrice,   // App.jsx가 매수단가로 사용
        buy_qty: +buyQty,       // App.jsx가 매수수량으로 사용
        prev_avg_price: avgPrice,
        prev_qty: holding.qty || 0,
        pnl_at_action: report?.micro?.pnl_pct || 0,
        risk_at_action: report?.payload?.risk || 0,
        support_at_action: report?.payload?.support || 0,
        created_at: new Date().toISOString(),
      });
      return;
    }

    // 매도
    if (!sellPrice || !sellQty) return alert("매도 가격과 수량을 입력해주세요");
    onSave({
      symbol: holding.symbol,
      name: holding.name,
      action_type: "partial_sell",
      alignment,
      phase_state: report?.state_stock || "WATCH",
      memo,
      avg_price: avgPrice,
      sell_price: +sellPrice,
      sell_qty: +sellQty,
      realized_pnl: realizedPnl,
      realized_pnl_pct: realizedPnlPct,
      pnl_at_action: report?.micro?.pnl_pct || 0,
      risk_at_action: report?.payload?.risk || 0,
      support_at_action: report?.payload?.support || 0,
      created_at: new Date().toISOString(),
    });
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200 }} onClick={onClose}>
      <div style={{ background: T.card, borderRadius: 16, padding: 28, maxWidth: 440, width: "90%", border: `1px solid ${T.border}`, maxHeight: "90vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <span style={{ fontSize: 22 }}>{ai.icon}</span>
          <span style={{ fontSize: 16, fontWeight: 800 }}>{holding.name} {ai.label}</span>
        </div>
        <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 20 }}>
          {isBuy
            ? "추가 매수를 기록하면 평균 단가와 보유 수량이 갱신됩니다."
            : "기록하면 종목이 포트폴리오에서 차감되고, 투자 여정에 기록돼요"}
        </div>

        {/* 입력 정보 */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>{isBuy ? "매수 정보" : "매도 정보"}</div>
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 8 }}>내 평균 매수가: {avgPrice.toLocaleString()}원 · 보유: {holding.qty}주</div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: T.textDim, marginBottom: 4 }}>{isBuy ? "매수 가격 (원)" : "매도 가격 (원)"}</div>
              {isBuy
                ? <Input placeholder="예: 85000" type="number" value={buyPrice} onChange={e => setBuyPrice(e.target.value)} />
                : <Input placeholder="예: 85000" type="number" value={sellPrice} onChange={e => setSellPrice(e.target.value)} />}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: T.textDim, marginBottom: 4 }}>{isBuy ? "매수 수량 (주)" : "매도 수량 (주)"}</div>
              {isBuy
                ? <Input placeholder="추가 매수 수량" type="number" value={buyQty} onChange={e => setBuyQty(e.target.value)} />
                : <Input placeholder={`${holding.qty}주`} type="number" value={sellQty} onChange={e => setSellQty(e.target.value)} />}
            </div>
          </div>

          {/* 매수: 새 평단가 미리보기 */}
          {isBuy && newAvgPreview && (
            <div style={{ marginTop: 10, padding: "10px 14px", borderRadius: 8, background: T.green + "15", border: `1px solid ${T.green}30` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 12, color: T.textDim }}>매수 후 평단가 / 수량</span>
                <div>
                  <span style={{ fontSize: 16, fontWeight: 800, color: T.text }}>{newAvgPreview.newAvg.toLocaleString()}원</span>
                  <span style={{ fontSize: 12, color: T.textDim, marginLeft: 8 }}>{newAvgPreview.totalQty}주</span>
                </div>
              </div>
            </div>
          )}

          {/* 매도: 실현 손익 */}
          {!isBuy && sellPrice && sellQty && (
            <div style={{ marginTop: 10, padding: "10px 14px", borderRadius: 8, background: realizedPnl >= 0 ? T.green + "15" : T.red + "15", border: `1px solid ${realizedPnl >= 0 ? T.green : T.red}30` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 12, color: T.textDim }}>실현 손익</span>
                <div>
                  <span style={{ fontSize: 16, fontWeight: 800, color: realizedPnl >= 0 ? T.green : T.red }}>
                    {realizedPnl >= 0 ? "+" : ""}{realizedPnl.toLocaleString()}원
                  </span>
                  <span style={{ fontSize: 12, color: realizedPnlPct >= 0 ? T.green : T.red, marginLeft: 8 }}>
                    ({realizedPnlPct >= 0 ? "+" : ""}{realizedPnlPct.toFixed(1)}%)
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>이 행동은 Phase 상태와 일치했나요?</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
          {alignments.map(a => (
            <div key={a.key} onClick={() => setAlignment(a.key)} style={{
              flex: 1, padding: "10px 8px", borderRadius: 10, textAlign: "center", cursor: "pointer",
              background: alignment === a.key ? a.color + "20" : T.bg, border: `1px solid ${alignment === a.key ? a.color : T.border}`,
              transition: "all 0.2s",
            }}>
              <div style={{ fontSize: 16 }}>{a.icon}</div>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.textDim, marginTop: 4 }}>{a.label}</div>
            </div>
          ))}
        </div>

        <div style={{ fontSize: 11, color: T.textDim, marginBottom: 4 }}>메모 (선택)</div>
        <Input placeholder="이유나 메모를 남겨보세요" value={memo} onChange={e => setMemo(e.target.value)} />

        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <Btn variant="ghost" style={{ flex: 1, justifyContent: "center" }} onClick={onClose}>취소</Btn>
          <Btn variant="primary" style={{ flex: 1, justifyContent: "center", background: ai.color, opacity: canSave ? 1 : 0.4 }} onClick={save} disabled={!canSave}>{ai.label} 기록</Btn>
        </div>
      </div>
    </div>
  );
}
